/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSBannerSplit } from '@/features/contentstack/components/CMSBannerSplit/CMSBannerSplit';
import { CMSCampaignModal } from '@/features/contentstack/components/CMSCampaignModal/CMSCampaignModal';
import { CMSCitrusCarouselShelf } from '@/features/contentstack/components/CMSCitrusCarousel/CMSCitrusCarouselShelf';
import { CMSCitrusShelf } from '@/features/contentstack/components/CMSCitrus/CMSCitrusShelf';
import { CMSExperienceInjector } from '@/features/contentstack/components/CMSExperienceInjector/CMSExperienceInjector';
import { CMSTarget } from '@/features/contentstack/components/CMSTarget/CMSTarget';
import type { ContentStackProductListingPage } from '@/features/contentstack/interface';
import { CMSPLPSpacer } from './CMSPLPSpacer/CMSPLPSpacer';
import { STARCTypography } from '@/components/STARCTypography';
import { CMSShelfYmme } from './CMSShelfYmme/CMSShelfYmme';
import { CMSResultList } from './CMSResultList/CMSResultList';
import { CMSSortingOptions } from './CMSSortingOptions/CMSSortingOptions';
import type { ShelfPart } from '@/types/legacy';
import type { HandleFilterType } from '@/types/filterTypes';
import { SuperDuperHero } from '@/features/home/components/SuperDuperHero/SuperDuperHero';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSCategoryGrid } from '@/features/contentstack/components/CMSCategoryGrid/CMSCategoryGrid';
import { CMSProductChart } from '@/features/contentstack/components/CMSProductChart/CMSProductChart';
import { CMSImageCategory } from '@/features/contentstack/components/CMSImageCategory/CMSImageCategory';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { useScrollIntoViewDOM } from '@/hooks/useScrollIntoView';
import { TopShelfAd } from './TopShelfAd/TopShelfAd';
import { BuyAgainCandidatesWithFallback } from '@/components/ActionCandidatesWithFallback/BuyAgainCandidatesWithFallback/BuyAgainCandidatesWithFallback';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useFeatureFlag } from '@/features/globalConfig';
import { CMSDealsSplit } from '@/features/contentstack/components/CMSDealsSplit/CMSDealsSplit';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type Props = {
  content: Array<NonNullable<ContentStackProductListingPage['shelfpagebody']>[0]>;
  pageType: 'shelf' | 'brand' | 'merch' | 'search';
  refQuickFilter?: React.Ref<HTMLElement>;
  resultsListRef?: React.RefObject<HTMLDivElement | null>;
  onFilterSelect?: (a: HandleFilterType) => void;
  makeModelYearPageType?: boolean;
  handleDealClick: (
    part: ShelfPart,
    price: string,
    quantity: number,
    ref: React.RefObject<HTMLButtonElement | null>,
    fulfillmentTypeId: number | null,
  ) => void;
};

export const CMSPLPBodyModularHandler = ({
  content,
  pageType,
  refQuickFilter,
  resultsListRef,
  onFilterSelect,
  handleDealClick,
  makeModelYearPageType,
}: Props) => {
  let foundSplitBanner = false;
  const enableBuyAgainOnPlpAndPdp = useForteDecisionFlagWithTrueDefault(
    'enableBuyAgainOnPlpAndPdp',
  );
  const buyAgainEndpointEnabled = useFeatureFlag('BUY_AGAIN_ENDPOINT_ENABLED') === 'true';
  const dateRange = Number(useFeatureFlag('BUY_AGAIN_DATE_RANGE'));
  useScrollIntoViewDOM();

  const { data: shelfData } = useProductList();
  const firstPart = shelfData?.shelfParts?.[0];
  const partTerminologyId = firstPart?.partTerminologyId;
  const partGroupId = firstPart?.partGroupId;

  return (
    <STARCTypography>
      {content.map((modularBlock, index) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if ('banner' in modularBlock && modularBlock.banner?.banner?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('banner')}>
              <CMSBanner
                // Only preload if there's no split banner above this banner
                priority={!foundSplitBanner}
                loading="eager"
                content={modularBlock.banner.banner[0]}
                legalPadding={{ s: true, m: true, l: false }}
              />
            </CMSPLPSpacer>
          );
        }

        if ('banner_split' in modularBlock && modularBlock.banner_split?.banner_split?.[0]) {
          foundSplitBanner = true;
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('banner_split')}>
              <CMSBannerSplit priority content={modularBlock.banner_split.banner_split[0]} />
            </CMSPLPSpacer>
          );
        }

        if (
          'experience_injector' in modularBlock &&
          modularBlock.experience_injector?.experience_injector?.[0]
        ) {
          return (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing={{ s: true, l: true }}
              removeVerticalSpacing
              id={getBlockId('experience_injector')}
            >
              <CMSExperienceInjector
                content={modularBlock.experience_injector.experience_injector[0]}
              />
            </CMSPLPSpacer>
          );
        }

        if ('ymme_content_block' in modularBlock && modularBlock.ymme_content_block) {
          const layout = modularBlock.ymme_content_block.layout ?? 'Thin';

          const maybeProductFinder = content.find(
            (block) => 'product_finder' in block && !!block.product_finder?.product_finder?.[0],
          );
          const maybeNullResult = content.find(
            (block) => 'null_result' in block && !!block.null_result?.null_result?.[0],
          );

          return (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing
              removeVerticalSpacing
              id={getBlockId('ymme_content_block')}
            >
              <CMSShelfYmme
                content={{ layout }}
                productFinderData={
                  maybeProductFinder && 'product_finder' in maybeProductFinder
                    ? maybeProductFinder?.product_finder?.product_finder?.[0]
                    : undefined
                }
                nullResultsData={
                  maybeNullResult && 'null_result' in maybeNullResult
                    ? maybeNullResult?.null_result?.null_result?.[0]
                    : undefined
                }
                makeModelYearPageType={makeModelYearPageType}
                pageType={pageType === 'search' ? 'search' : undefined}
              />
            </CMSPLPSpacer>
          );
        }

        if ('citrus_ad' in modularBlock && modularBlock.citrus_ad?.citrus_ad?.[0]) {
          return modularBlock.citrus_ad.citrus_ad[0].type === 'carousel' ? (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing={{ s: true, l: true }}
              id={getBlockId('citrus_ad')}
            >
              <CMSCitrusCarouselShelf />
            </CMSPLPSpacer>
          ) : (
            <div id={getBlockId('citrus_ad')} key={`${pageType}-${index}`}>
              <CMSCitrusShelf content={modularBlock.citrus_ad.citrus_ad[0]} />
            </div>
          );
        }

        if ('ad' in modularBlock && modularBlock.ad?.citrus_ad?.[0]) {
          return modularBlock.ad.citrus_ad[0].type === 'carousel' ? (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing={{ s: true, l: true }}
              id={getBlockId('ad')}
            >
              <CMSCitrusCarouselShelf />
            </CMSPLPSpacer>
          ) : (
            <div id={getBlockId('ad')} key={`${pageType}-${index}`}>
              <TopShelfAd content={modularBlock.ad} />
            </div>
          );
        }

        if ('target_div' in modularBlock && modularBlock.target_div?.target_div?.[0]) {
          return (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing={{ s: true, l: true }}
              removeVerticalSpacing
              id={getBlockId('target_div')}
            >
              <CMSTarget content={modularBlock.target_div.target_div[0]} />
            </CMSPLPSpacer>
          );
        }

        if ('results_list' in modularBlock && modularBlock.results_list) {
          return (
            <CMSPLPSpacer
              key={`${pageType}-${index}`}
              removeHorizontalSpacing
              id={getBlockId('results_list')}
            >
              <CMSSortingOptions
                refQuickFilter={refQuickFilter}
                onFilterSelect={onFilterSelect}
                resultsListRef={resultsListRef}
              />
              <CMSResultList resultsListRef={resultsListRef} handleDealClick={handleDealClick} />
            </CMSPLPSpacer>
          );
        }

        if (
          'super_duper_hero' in modularBlock &&
          modularBlock.super_duper_hero?.super_duper_hero?.[0]
        ) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('super_duper_hero')}>
              <SuperDuperHero options={modularBlock.super_duper_hero.super_duper_hero[0]} />
            </CMSPLPSpacer>
          );
        }

        if ('campaign' in modularBlock && modularBlock.campaign?.campaign) {
          return (
            <div id={getBlockId('campaign')} key={`${pageType}-${index}`}>
              <CMSCampaignModal content={modularBlock.campaign.campaign} />
            </div>
          );
        }

        if ('product_grid' in modularBlock && modularBlock.product_grid?.product_grid?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('product_grid')}>
              <CMSProductTypeGrid
                content={modularBlock.product_grid.product_grid[0]}
                gridIndex={index}
              />
            </CMSPLPSpacer>
          );
        }

        if ('blogtype_grid' in modularBlock && modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('blogtype_grid')}>
              <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
            </CMSPLPSpacer>
          );
        }

        if ('category_grid' in modularBlock && modularBlock.category_grid?.category_grid?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('category_grid')}>
              <CMSCategoryGrid content={modularBlock.category_grid.category_grid[0]} />
            </CMSPLPSpacer>
          );
        }

        if ('product_chart' in modularBlock && modularBlock.product_chart?.product_chart?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('product_chart')}>
              <CMSProductChart content={modularBlock.product_chart.product_chart[0]} />
            </CMSPLPSpacer>
          );
        }

        if (
          'image_category_grid' in modularBlock &&
          modularBlock.image_category_grid?.image_category_grid?.[0]
        ) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('image_category_grid')}>
              <CMSImageCategory content={modularBlock.image_category_grid.image_category_grid[0]} />
            </CMSPLPSpacer>
          );
        }

        if (modularBlock.deals_split?.deals_split?.[0]) {
          return (
            <CMSPLPSpacer key={`${pageType}-${index}`} id={getBlockId('deals_split')}>
              <CMSDealsSplit content={modularBlock.deals_split?.deals_split[0]} />
            </CMSPLPSpacer>
          );
        }

        if (
          enableBuyAgainOnPlpAndPdp &&
          buyAgainEndpointEnabled &&
          'buy_again_container' in modularBlock &&
          modularBlock.buy_again_container?.buy_again_container?.[0]
        ) {
          const buyAgainContainer = modularBlock.buy_again_container.buy_again_container[0];
          return (
            <div id={getBlockId('buy_again_container')} key={`${pageType}-${index}`}>
              <BuyAgainCandidatesWithFallback
                limit={buyAgainContainer.product_buy_again_limit ?? 0}
                title={buyAgainContainer.buy_again_title ?? ''}
                dateRange={dateRange}
                fallbackBanner={buyAgainContainer.fallback_banner?.[0]}
                partTerminologyId={partTerminologyId}
                partGroupId={partGroupId}
                SpacerComponent={CMSPLPSpacer}
                sessionStorageKey="showPLPBuyAgainCandidates"
              />
            </div>
          );
        }

        if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
          return (
            <CMSPLPSpacer
              key={modularBlock.pencil_banner.pencil_banner[0].uid}
              {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                id: modularBlock.pencil_banner.pencil_banner[0].component_id,
              })}
            >
              <CMSPencilBanner content={modularBlock.pencil_banner?.pencil_banner?.[0]} />
            </CMSPLPSpacer>
          );
        }
      })}
    </STARCTypography>
  );
};
